// ═══════════════════════════════════════════════════════════════
// PublicUnsubscribePage.jsx — Landing page for the unsubscribe
// link in notification emails.
//
//   ?action=unsubscribe&token=...
//
// The signed token is the authorization, so no login is required.
// Renders OUTSIDE the main app providers, same as PublicDeletionPage.
//
// The opt-out runs automatically on load rather than behind a button,
// so the link is effectively one-click. It is still a POST, which
// keeps mail scanners and link prefetchers from unsubscribing people
// who never clicked anything. Resubscribe is offered right there so
// a misclick costs nothing.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, CheckCircle, AlertTriangle, BellOff, Bell } from 'lucide-react';
import { STRIPE_CONFIG, OWL_LOGO } from '../shared.jsx';

async function callUnsubscribe(token, resubscribe) {
  const response = await fetch(`${STRIPE_CONFIG.cloudFunctionUrl}/unsubscribeEmail`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, resubscribe }),
  });
  return { status: response.status, body: await response.json().catch(() => ({})) };
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full">
        <div className="text-center mb-6">
          <img src={OWL_LOGO} alt="" className="w-12 h-12 rounded-xl mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-stone-800">The ArguMentor</h1>
        </div>
        <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-6">
          {children}
        </div>
        <p className="text-center text-xs text-stone-500 mt-4">
          Need help? Contact <a href="mailto:info@4hci.com" className="text-amber-700 hover:underline">info@4hci.com</a>
        </p>
      </div>
    </div>
  );
}

function PublicUnsubscribePage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [phase, setPhase] = useState('working'); // working | done | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const ranRef = useRef(false);

  const submit = useCallback(async (resubscribe) => {
    setBusy(true);
    try {
      const { status, body } = await callUnsubscribe(token, resubscribe);
      if (status === 200 && body.success) {
        setResult(body);
        setPhase('done');
      } else {
        setErrorMsg(body.error || 'Something went wrong. Please try again.');
        setPhase('error');
      }
    } catch (e) {
      setErrorMsg('Could not reach the server. Please check your connection and try again.');
      setPhase('error');
    }
    setBusy(false);
  }, [token]);

  useEffect(() => {
    // StrictMode double-invokes effects in development; guard so the
    // opt-out is not submitted twice.
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) {
      setErrorMsg('This unsubscribe link is missing its code. Open your notification settings in the app instead.');
      setPhase('error');
      return;
    }
    submit(false);
  }, [token, submit]);

  if (phase === 'working') {
    return (
      <PageShell>
        <div className="text-center py-6" role="status">
          <Loader2 className="w-8 h-8 text-amber-700 mx-auto mb-3 animate-spin" />
          <p className="text-stone-600">Updating your email preferences...</p>
        </div>
      </PageShell>
    );
  }

  if (phase === 'error') {
    return (
      <PageShell>
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-amber-700 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-stone-800 mb-2">We couldn't update that</h2>
          <p className="text-sm text-stone-600 mb-5">{errorMsg}</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-stone-900 text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
          >
            Open The ArguMentor
          </a>
        </div>
      </PageShell>
    );
  }

  const subscribed = !!result?.subscribed;
  const label = result?.categoryLabel || 'these emails';

  return (
    <PageShell>
      <div className="text-center">
        {subscribed ? (
          <Bell className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
        ) : (
          <BellOff className="w-8 h-8 text-stone-500 mx-auto mb-3" />
        )}
        <h2 className="text-lg font-semibold text-stone-800 mb-2">
          {subscribed ? 'You’re subscribed again' : 'You’re unsubscribed'}
        </h2>
        <p className="text-sm text-stone-600 mb-1">
          {subscribed
            ? <>We’ll send you {label} again{result?.email ? <> at <span className="font-medium text-stone-800">{result.email}</span></> : null}.</>
            : <>We’ve stopped sending {label}{result?.email ? <> to <span className="font-medium text-stone-800">{result.email}</span></> : null}.</>}
        </p>
        {!subscribed && (
          <p className="text-xs text-stone-500 mb-5">
            Account and billing messages still come through, since those aren't promotional.
          </p>
        )}

        <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
          <button
            onClick={() => submit(!subscribed)}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 disabled:opacity-60 text-stone-700 text-sm font-medium rounded-lg transition-colors"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {subscribed ? 'Unsubscribe again' : 'Undo, keep sending them'}
          </button>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-stone-900 text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            Manage all settings
          </a>
        </div>
        <p className="text-xs text-stone-500 mt-4">
          You can change every email category any time from your profile in the app.
        </p>
      </div>
    </PageShell>
  );
}

export default PublicUnsubscribePage;
